import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import axios from "axios";
import { setUserProductCategories } from "../../redux/Actions/productCategoriesAction";
import { setCartCount, deleteCartCount } from "../../redux/Actions/cartCountAction";
import { setValue } from "../../utility";
import "./pricingListRevamp.css";

const base_url = process.env.REACT_APP_BACKEND_URL;

function PricingListRevamp() {
  const [categories, setCategories] = useState<any[]>([]);
  const [openCategory, setOpenCategory] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const dispatch = useDispatch<any>();
  const settings: any = useSelector<any>((state) => state.settings);
  const currency = settings?.WebmasterSettings?.currency || "£";

  // Redux cart (shared with header cart panel + checkout)
  const selectedCategories: any = useSelector<any>(
    (state) => state.userSelectedCategories
  );
  const { cartCount }: any = useSelector<any>((state) => state);

  useEffect(() => {
    const headers = {
      Accept: "application/json",
      "Access-Control-Allow-Origin": "*",
    };

    axios
      .get(`${base_url}/api/get_categories?group_id=`, { headers })
      .then((res) => {
        const list = res.data?.data?.category || [];
        setCategories(list);
        if (list.length) setOpenCategory(list[0].main_category_name);
        setLoading(false);
      })
      .catch(() => {
        setError(true);
        setLoading(false);
      });
  }, []);

  const toggle = (name: string) =>
    setOpenCategory((prev) => (prev === name ? null : name));

  // --- cart helpers --------------------------------------------------------
  const findCategory = (list: any[], mainId: any) =>
    list?.find((c: any) => String(c.main_category_id) === String(mainId));

  const getQuantity = (mainCategory: any, subCategory: any) => {
    const cat = findCategory(
      Array.isArray(selectedCategories) ? selectedCategories : [],
      mainCategory.main_category_id
    );
    const sub = cat?.sub_categories?.find(
      (s: any) =>
        String(s.sub_category_id) === String(subCategory.sub_category_id)
    );
    return parseInt(sub?.quantity || "0");
  };

  const updateCart = (mainCategory: any, subCategory: any, delta: number) => {
    const mainId = String(mainCategory.main_category_id);
    const subId = String(subCategory.sub_category_id);
    let list: any[] = Array.isArray(selectedCategories)
      ? [...selectedCategories]
      : [];

    // ensure the main category exists in the cart list
    let cat = findCategory(list, mainId);
    if (!cat) {
      cat = { ...mainCategory };
      list = [...list, cat];
    }

    // ensure the sub category exists under it
    let subs: any[] = cat.sub_categories ? [...cat.sub_categories] : [];
    let sub = subs.find(
      (s: any) => String(s.sub_category_id) === subId
    );
    if (!sub) {
      sub = { ...subCategory, quantity: "0" };
      subs = [...subs, sub];
    }

    const currentQty = parseInt(sub.quantity || "0");
    const newQty = Math.max(0, currentQty + delta);
    if (newQty === currentQty) return; // e.g. remove pressed at 0

    const updatedCat = {
      ...cat,
      sub_categories: subs.map((s: any) =>
        String(s.sub_category_id) === subId
          ? { ...s, quantity: String(newQty) }
          : s
      ),
    };
    const updatedList = list.map((c: any) =>
      String(c.main_category_id) === mainId ? updatedCat : c
    );

    // push into the shared cart + header count
    dispatch(setUserProductCategories(updatedList));
    dispatch(delta > 0 ? setCartCount(cartCount + 1) : deleteCartCount(1));

    // persist so the cart survives into the product list / checkout flow
    const cartInformationData = updatedList.filter((c: any) =>
      c.sub_categories?.some((s: any) => parseInt(s.quantity) > 0)
    );
    setValue("cartInformationData", cartInformationData);
    localStorage.removeItem("is_removed");
  };

  return (
    <div className="plr">
      <div className="plr__inner">
        <div className="plr__head">
          <span className="plr__eyebrow">Our Rates</span>
          <h2 className="plr__title">Pricing &amp; List</h2>
        </div>

        {loading && (
          <div className="plr__state">
            <div className="plr__spinner" />
            Loading pricing…
          </div>
        )}

        {!loading && error && (
          <div className="plr__state">
            Unable to load pricing right now. Please try again later.
          </div>
        )}

        {!loading && !error && categories.length === 0 && (
          <div className="plr__state">No pricing available at the moment.</div>
        )}

        {!loading && !error && categories.length > 0 && (
          <div className="plr__list">
            {categories.map((category: any, i: number) => {
              const isOpen = openCategory === category.main_category_name;
              const subs = category.sub_categories || [];
              return (
                <div
                  className={isOpen ? "plr__panel is-open" : "plr__panel"}
                  key={i}
                >
                  <button
                    type="button"
                    className="plr__panel-head"
                    onClick={() => toggle(category.main_category_name)}
                  >
                    <span className="plr__panel-title">
                      {category.main_category_name}
                      <span className="plr__panel-count">
                        {subs.length} items
                      </span>
                    </span>
                    <span className="plr__chev">&#9660;</span>
                  </button>

                  <div className="plr__panel-body">
                    {subs.map((sub_category: any, si: number) => {
                      const quantity = getQuantity(category, sub_category);
                      return (
                        <div className="plr__row" key={si}>
                          <span
                            className="plr__row-name"
                            title={(sub_category.options || [])
                              .filter((o: any) => o.is_default == "1")
                              .map(
                                (o: any) =>
                                  `${o.option_description} : ${o.option_price}`
                              )
                              .join(", ")}
                          >
                            {sub_category.sub_category_name}
                          </span>
                          <span className="plr__row-dots" />
                          <span className="plr__row-price">
                            {currency}
                            {sub_category.total_price}
                          </span>

                          {quantity > 0 ? (
                            <span className="plr__stepper">
                              <button
                                type="button"
                                className="plr__step-btn"
                                aria-label="Remove one"
                                onClick={() =>
                                  updateCart(category, sub_category, -1)
                                }
                              >
                                &minus;
                              </button>
                              <span className="plr__step-qty">{quantity}</span>
                              <button
                                type="button"
                                className="plr__step-btn"
                                aria-label="Add one"
                                onClick={() =>
                                  updateCart(category, sub_category, 1)
                                }
                              >
                                +
                              </button>
                            </span>
                          ) : (
                            <button
                              type="button"
                              className="plr__add-btn"
                              onClick={() =>
                                updateCart(category, sub_category, 1)
                              }
                            >
                              Add
                            </button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

export default PricingListRevamp;
